import { Sparkles } from 'lucide-react-native';
import { useCallback, useRef } from 'react';
import { StyleSheet } from 'react-native';
import { Box, HStack, StatusBanner, Text, VStack } from '../../../components/primitives';
import { Button } from '../../../components/primitives/Button';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import { AgentToggleRow } from '../../agents/components/AgentToggleRow';
import { CustomInstructionsEditor } from '../../agents/components/CustomInstructionsEditor';
import { SkillToggleCard } from '../../agents/components/SkillToggleCard';
import { isOrgConfigurableAgent } from '../../agents/constants/configurable-agents';
import { isCustomizableAgent } from '../../agents/constants/customizable-agents';
import { type OrgAgentSkill, SkillExecutionMode } from '../../agents/types';
import {
  type GeneratedPlan,
  type GeneratedPlanSkill,
  ONBOARDING_CONFIDENCE_FALLBACK_THRESHOLD,
} from '../types';

// Mirrors AgentConfigCard's agentLabels map (common.agentTypes.*), extended
// with a humanized fallback for any agent type the catalog doesn't know
// about yet.
const AGENT_TYPE_LABEL_KEYS: Record<string, string> = {
  lead_qualification: 'common.agentTypes.leadQualification',
  lead_enrichment: 'common.agentTypes.leadEnrichment',
  follow_up_scheduler: 'common.agentTypes.followUpScheduler',
  sentiment_analysis: 'common.agentTypes.sentimentAnalysis',
  prompt_enricher: 'common.agentTypes.promptEnricher',
  linkedin_company_research: 'common.agentTypes.linkedinCompanyResearch',
  browser_company_research: 'common.agentTypes.browserCompanyResearch',
  instagram_company_research: 'common.agentTypes.instagramCompanyResearch',
};

function humanize(value: string): string {
  return value
    .split('_')
    .map((word) => (word.length > 0 ? word[0].toUpperCase() + word.slice(1) : word))
    .join(' ');
}

// SkillToggleCard/OrgAgentSkill expects a full catalog entity. GeneratedPlan
// skills are a minimal { name, enabled, rationale } proposal, so this adapts
// one into the other — the LLM `rationale` is surfaced via `description`
// (the field SkillToggleCard actually renders).
function toOrgAgentSkill(skill: GeneratedPlanSkill): OrgAgentSkill {
  return {
    id: skill.name,
    name: skill.name,
    agentType: '',
    description: skill.rationale,
    promptSnippet: '',
    category: 'general',
    isDefault: true,
    sortOrder: 0,
    organizationId: null,
    toolNames: [],
    executionMode: SkillExecutionMode.PROMPT_ONLY,
    maxToolCalls: 0,
    createdAt: '',
    updatedAt: '',
    enabled: skill.enabled,
    isCustom: false,
  };
}

interface ReviewPlanStepProps {
  organizationId: string;
  plan: GeneratedPlan;
  onChange: (plan: GeneratedPlan) => void;
  /** Receives the plan with every pending editor draft already folded in. */
  onApply: (plan: GeneratedPlan) => void;
  isApplying: boolean;
}

export function ReviewPlanStep({
  organizationId,
  plan,
  onChange,
  onApply,
  isApplying,
}: Readonly<ReviewPlanStepProps>) {
  const { t } = useTranslation();
  const isFallback = plan.confidence < ONBOARDING_CONFIDENCE_FALLBACK_THRESHOLD;

  // A plan persisted before the backend narrowed its catalog can still name agents
  // that have no toggle on the Agents screen — stage_*, the legacy *_enrichment
  // kinds, the internal research agent, the chat orchestrator. Showing one here
  // hands the user a switch they can never find again, and for a stage agent one
  // that changes nothing at all (those are gated by StageAgentConfig, not
  // enabledAgents). Apply drops them server-side; this keeps them off the screen.
  const visibleAgents = plan.agents.filter((agent) => isOrgConfigurableAgent(agent.type));

  // Pending editor text, keyed by agent type. Held in a ref rather than state
  // on purpose: writing it into `plan` on every keystroke would churn each
  // editor's `initialValue` and reset its AI-undo history mid-typing.
  const draftsRef = useRef<Record<string, string>>({});

  const handleToggleAgent = (type: string) => {
    onChange({
      ...plan,
      agents: plan.agents.map((agent) =>
        agent.type === type ? { ...agent, enabled: !agent.enabled } : agent,
      ),
    });
  };

  const handleSaveCustomInstructions = (type: string, customInstructions: string) => {
    draftsRef.current[type] = customInstructions;
    onChange({
      ...plan,
      agents: plan.agents.map((agent) =>
        agent.type === type ? { ...agent, customInstructions } : agent,
      ),
    });
  };

  const handleDraftCustomInstructions = (type: string, customInstructions: string) => {
    draftsRef.current[type] = customInstructions;
  };

  // Apply is the real commit, so it has to carry what is on screen right now —
  // including boxes the user typed into and never pressed Save on. Drafts only
  // count for agents that are both enabled and actually customizable; the
  // backend discards the rest, and a disabled agent's editor is not shown.
  const handleApply = useCallback(() => {
    const drafts = draftsRef.current;

    onApply({
      ...plan,
      agents: plan.agents.map((agent) => {
        const draft = drafts[agent.type];
        const isPending =
          draft !== undefined &&
          draft !== agent.customInstructions &&
          agent.enabled &&
          isCustomizableAgent(agent.type);

        return isPending ? { ...agent, customInstructions: draft } : agent;
      }),
    });
  }, [plan, onApply]);

  const handleToggleSkill = (name: string, enabled: boolean) => {
    onChange({
      ...plan,
      skills: plan.skills.map((skill) => (skill.name === name ? { ...skill, enabled } : skill)),
    });
  };

  return (
    <VStack spacing="lg" testID="onboarding-review-step">
      <Text variant="h4" color={colors.text.primary}>
        {t('onboardingWizard.review.title')}
      </Text>

      {isFallback && (
        <StatusBanner
          testID="onboarding-review-fallback-banner"
          variant="warning"
          message={t('onboardingWizard.review.fallbackBanner')}
        />
      )}

      <VStack spacing="sm">
        <Text variant="h6" color={colors.text.secondary}>
          {t('onboardingWizard.review.agentsTitle')}
        </Text>
        {visibleAgents.map((agent) => {
          const label = AGENT_TYPE_LABEL_KEYS[agent.type]
            ? t(AGENT_TYPE_LABEL_KEYS[agent.type])
            : humanize(agent.type);

          return (
            <VStack spacing="xs" key={agent.type}>
              <AgentToggleRow
                label={label}
                icon={Sparkles}
                iconColor={colors.primary[400]}
                enabled={agent.enabled}
                onPress={() => handleToggleAgent(agent.type)}
                onLabel={t('common.states.on')}
                offLabel={t('common.states.off')}
              />
              <Text variant="caption" color={colors.text.tertiary} style={styles.rationale}>
                {agent.rationale}
              </Text>
              {/* Only agents the backend will actually persist instructions
                  for get an editor — see constants/customizable-agents.ts.
                  Offering one for the rest silently discarded the text. */}
              {agent.enabled && isCustomizableAgent(agent.type) && (
                <CustomInstructionsEditor
                  organizationId={organizationId}
                  agentType={agent.type}
                  initialValue={agent.customInstructions ?? ''}
                  onSave={(value) => handleSaveCustomInstructions(agent.type, value)}
                  onDraftChange={(value) => handleDraftCustomInstructions(agent.type, value)}
                  isSaving={false}
                />
              )}
            </VStack>
          );
        })}
      </VStack>

      <VStack spacing="sm">
        <Text variant="h6" color={colors.text.secondary}>
          {t('onboardingWizard.review.skillsTitle')}
        </Text>
        {plan.skills.map((skill) => (
          <SkillToggleCard
            key={skill.name}
            skill={toOrgAgentSkill(skill)}
            onToggle={handleToggleSkill}
            isToggling={false}
            testID={`onboarding-skill-${skill.name}`}
          />
        ))}
      </VStack>

      {plan.toolsPreview.length > 0 && (
        <VStack spacing="sm">
          <Text variant="h6" color={colors.text.secondary}>
            {t('onboardingWizard.review.toolsTitle')}
          </Text>
          <HStack spacing="xs" style={styles.toolsRow}>
            {plan.toolsPreview.map((toolName) => (
              <Box
                key={toolName}
                backgroundColor={colors.background.tertiary}
                borderRadius="full"
                paddingX="sm"
                paddingY="xxs"
              >
                <Text variant="caption" color={colors.text.tertiary}>
                  {toolName}
                </Text>
              </Box>
            ))}
          </HStack>
        </VStack>
      )}

      {plan.voiceAgent && (
        <VStack spacing="xs" testID="onboarding-review-voice">
          <Text variant="h6" color={colors.text.secondary}>
            {t('onboardingWizard.review.voiceTitle')}
          </Text>
          <Text variant="body" color={colors.text.primary}>
            {plan.voiceAgent.name}
          </Text>
          <Text variant="caption" color={colors.text.tertiary}>
            {t('onboardingWizard.review.voiceUseCase')}: {plan.voiceAgent.useCase}
          </Text>
          {plan.voiceAgent.successCriteria.length > 0 && (
            <Text variant="caption" color={colors.text.tertiary}>
              {t('onboardingWizard.review.voiceSuccessCriteria')}:{' '}
              {plan.voiceAgent.successCriteria.join(', ')}
            </Text>
          )}
        </VStack>
      )}

      <Button
        testID="onboarding-review-apply"
        variant="primary"
        onPress={handleApply}
        disabled={isApplying}
        loading={isApplying}
        accessibilityState={{ disabled: isApplying }}
      >
        {isApplying ? t('onboardingWizard.review.applying') : t('onboardingWizard.review.apply')}
      </Button>
    </VStack>
  );
}

const styles = StyleSheet.create({
  rationale: {
    paddingLeft: tokens.spacing.sm,
  },
  toolsRow: {
    flexWrap: 'wrap',
  },
});
